import { useState } from 'react';
import { Tag, UserMinus } from 'lucide-react';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, ConfirmDialog } from '@vnetwork/ui/feedback';
import { Card, CardBody } from '@vnetwork/ui/primitives';
import {
  useClassifyLeadType,
  useUpdateCustomerStatus,
  type CustomerLeadType,
} from '@/api/customer-basic';

/** Extract a human-readable message from an axios/unknown error. */
function errorMessage(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string } } })
    ?.response;
  return res?.data?.error_description ?? res?.data?.error ?? 'Something went wrong.';
}

const LEAD_TYPE_OPTIONS: { value: CustomerLeadType; label: string }[] = [
  { value: 'warm-lead', label: 'Warm lead' },
  { value: 'sale-qualified-lead', label: 'Sales-qualified lead' },
  { value: 'cool-lead', label: 'Cool lead' },
  { value: 'un-qualified-lead', label: 'Unqualified lead' },
];

interface LeadActionsBarProps {
  customerId: number;
  customerStatus: string | undefined;
}

/**
 * Classify Lead + Unfollow Lead action bar — displayed only for cool-lead and
 * qualified-prospect customers on the Basic Information tab.
 *
 * - Classify Lead: PUT /customers/:id/classify-lead with a selected lead_type.
 * - Unfollow Lead: PUT /customers/:id/status → dead-lead (with an optional note).
 *
 * USER runs the live mutations; the assistant wires the UI only.
 */
export function LeadActionsBar({ customerId, customerStatus: _customerStatus }: LeadActionsBarProps) {
  const [classifyOpen, setClassifyOpen] = useState(false);
  const [unfollowOpen, setUnfollowOpen] = useState(false);
  const [leadType, setLeadType] = useState<CustomerLeadType>('warm-lead');

  const classify = useClassifyLeadType(customerId);
  const unfollow = useUpdateCustomerStatus(customerId);

  const submitClassify = () => {
    classify.mutate(leadType, {
      onSuccess: () => { setClassifyOpen(false); classify.reset(); },
    });
  };

  const submitUnfollow = () => {
    unfollow.mutate(
      { customer_status: 'dead-lead' },
      { onSuccess: () => { setUnfollowOpen(false); unfollow.reset(); } },
    );
  };

  return (
    <Card flat>
      <CardBody>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
          <Button
            variant="secondary"
            startIcon={<Tag size={15} />}
            onClick={() => setClassifyOpen(true)}
          >
            Classify Lead
          </Button>
          <Button
            variant="secondary"
            startIcon={<UserMinus size={15} />}
            onClick={() => setUnfollowOpen(true)}
          >
            Unfollow Lead
          </Button>
        </div>
      </CardBody>

      {/* Classify Lead dialog */}
      <ConfirmDialog
        open={classifyOpen}
        onOpenChange={(o) => { if (!classify.isPending) { setClassifyOpen(o); if (!o) classify.reset(); } }}
        title="Classify Lead Type"
        description="Select the lead type to assign to this customer."
        confirmLabel="Classify"
        loading={classify.isPending}
        onConfirm={submitClassify}
      >
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {classify.isError ? (
            <Alert tone="danger" title="Could not classify">{errorMessage(classify.error)}</Alert>
          ) : null}
          <Field label="Lead type" required>
            {(p) => (
              <Select value={leadType} onValueChange={(v) => setLeadType(v as CustomerLeadType)}>
                <SelectTrigger {...p}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
        </div>
      </ConfirmDialog>

      {/* Unfollow Lead dialog */}
      <ConfirmDialog
        open={unfollowOpen}
        onOpenChange={(o) => { if (!unfollow.isPending) { setUnfollowOpen(o); if (!o) unfollow.reset(); } }}
        title="Unfollow Lead"
        description="Mark this customer as Dead Lead. This removes them from the active lead pipeline."
        confirmLabel="Unfollow"
        destructive
        loading={unfollow.isPending}
        onConfirm={submitUnfollow}
      >
        {unfollow.isError ? (
          <Alert tone="danger" title="Could not unfollow">{errorMessage(unfollow.error)}</Alert>
        ) : null}
      </ConfirmDialog>
    </Card>
  );
}
